import React, { useState, useEffect } from 'react';
import { FiPlay, FiPause, FiSquare } from 'react-icons/fi';
import { toast } from 'react-toastify';
import timeTrackingService from '../../services/timeTrackingService';

const TimeEntryTimer = ({ onTimerStop, projects, tasks }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [currentTimer, setCurrentTimer] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedTask, setSelectedTask] = useState('');
  const [description, setDescription] = useState('');

  // Load running timer on mount
  useEffect(() => {
    loadRunningTimer();
  }, []);

  // Update elapsed time every second when timer is running
  useEffect(() => {
    let interval;
    if (isRunning && currentTimer) {
      interval = setInterval(() => {
        const start = new Date(currentTimer.start_time);
        const now = new Date();
        const elapsed = (now - start) / 1000; // seconds
        setElapsedTime(elapsed);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning, currentTimer]);

  const loadRunningTimer = async () => {
    try {
      const response = await timeTrackingService.getRunningTimer();
      if (response.data) {
        setCurrentTimer(response.data);
        setIsRunning(true);
        setSelectedProject(response.data.project_id);
        setSelectedTask(response.data.task_id || '');
        setDescription(response.data.description || '');
      }
    } catch (error) {
      console.error('Error loading running timer:', error);
    }
  };

  const handleStart = async () => {
    if (!selectedProject) {
      toast.error('Please select a project');
      return;
    }

    try {
      const response = await timeTrackingService.startTimer({
        project_id: selectedProject,
        task_id: selectedTask || null,
        description
      });

      setCurrentTimer(response.data);
      setIsRunning(true);
      toast.success('Timer started');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to start timer');
    }
  };

  const handleStop = async () => {
    if (!currentTimer) return;

    try {
      const response = await timeTrackingService.stopTimer(currentTimer.id);
      setIsRunning(false);
      setCurrentTimer(null);
      setElapsedTime(0);
      setSelectedProject('');
      setSelectedTask('');
      setDescription('');
      toast.success('Timer stopped');
      
      if (onTimerStop) {
        onTimerStop(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to stop timer');
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const filteredTasks = tasks?.filter(task => 
    !selectedProject || task.project_id === selectedProject
  ) || [];

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <h2 className="text-xl font-semibold mb-4 flex items-center">
        <FiPlay className="mr-2" />
        Time Tracker
      </h2>

      {isRunning ? (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm text-gray-600">Currently tracking</p>
                <p className="font-semibold text-lg">
                  {currentTimer?.project?.name}
                  {currentTimer?.task && ` - ${currentTimer.task.title}`}
                </p>
                {currentTimer?.description && (
                  <p className="text-sm text-gray-600 mt-1">{currentTimer.description}</p>
                )}
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-blue-600">
                  {formatTime(elapsedTime)}
                </div>
                <p className="text-xs text-gray-500">
                  Started: {new Date(currentTimer?.start_time).toLocaleTimeString()}
                </p>
              </div>
            </div>
            <button
              onClick={handleStop}
              className="w-full mt-3 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition flex items-center justify-center"
            >
              <FiSquare className="mr-2" />
              Stop Timer
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Project *
              </label>
              <select
                value={selectedProject}
                onChange={(e) => {
                  setSelectedProject(e.target.value);
                  setSelectedTask(''); // Reset task when project changes
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Select a project</option>
                {projects?.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Task (Optional)
              </label>
              <select
                value={selectedTask}
                onChange={(e) => setSelectedTask(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={!selectedProject}
              >
                <option value="">No task selected</option>
                {filteredTasks.map(task => (
                  <option key={task.id} value={task.id}>
                    {task.title}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (Optional)
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What are you working on?"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <button
            onClick={handleStart}
            disabled={!selectedProject}
            className="w-full bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center text-lg font-semibold"
          >
            <FiPlay className="mr-2" />
            Start Timer
          </button>
        </div>
      )}
    </div>
  );
};

export default TimeEntryTimer;

